import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Copy, Check, Edit3, Trash2, ExternalLink, ArrowLeft, BookMarked } from "lucide-react";
import { toast } from "sonner";

const CookbookManage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [editing, setEditing] = useState(null); // { share_id, title }

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    api.get("/cookbook/shares/mine")
      .then((res) => setShares(res.data?.shares || []))
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const copyLink = async (shareId) => {
    const url = `${window.location.origin}/cookbook/shared/${shareId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 1800);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const saveTitle = async () => {
    if (!editing) return;
    const title = editing.title.trim();
    if (!title) return;
    try {
      await api.patch(`/cookbook/shares/${editing.share_id}`, { title });
      setShares((prev) => prev.map((s) => (s.share_id === editing.share_id ? { ...s, title } : s)));
      toast.success("Title updated");
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const revokeShare = async (shareId) => {
    if (!window.confirm("Revoke this link? The URL will stop working immediately.")) return;
    try {
      await api.delete(`/cookbook/shares/${shareId}`);
      setShares((prev) => prev.filter((s) => s.share_id !== shareId));
      toast.success("Link revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-12">
        <Link
          to="/cookbook"
          data-testid="manage-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link mb-12"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to cookbook
        </Link>

        <div className="mb-16 border-b border-stone-800 pb-12">
          <div className="flex items-center gap-4 mb-6">
            <BookMarked className="w-4 h-4 text-orange-500" />
            <span className="eyebrow">Manage shares</span>
          </div>
          <h1
            data-testid="manage-title"
            className="font-serif-display text-6xl md:text-7xl text-stone-100 leading-[0.9] tracking-tighter"
          >
            Every link you&apos;ve <span className="italic text-orange-600">let loose</span>.
          </h1>
          <p className="mt-6 max-w-xl text-sm text-stone-400 leading-relaxed">
            Rename them, copy them, or revoke the ones you regret sending your mother-in-law.
          </p>
        </div>

        {shares.length === 0 ? (
          <div className="text-center py-24 border border-stone-800">
            <p className="font-serif-display italic text-2xl text-stone-400">
              You haven&apos;t shared any cookbooks yet.
            </p>
            <Link
              to="/cookbook"
              className="mt-6 inline-block text-orange-500 uppercase text-xs tracking-[0.24em] ink-link"
            >
              Head to your cookbook to make one
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="manage-list">
            {shares.map((s) => {
              const shareUrl = `${window.location.origin}/cookbook/shared/${s.share_id}`;
              const isEditing = editing?.share_id === s.share_id;
              return (
                <div
                  key={s.share_id}
                  data-testid={`manage-row-${s.share_id}`}
                  className="border border-stone-800 bg-stone-900/40 p-6 md:p-8"
                >
                  <div className="flex flex-wrap items-start gap-6 justify-between">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={editing.title}
                            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                            maxLength={120}
                            data-testid={`manage-edit-input-${s.share_id}`}
                            className="flex-1 bg-stone-950 border border-stone-800 focus:border-orange-600 text-stone-100 px-4 py-2.5 outline-none font-serif-display text-xl"
                          />
                          <button
                            type="button"
                            onClick={saveTitle}
                            data-testid={`manage-save-${s.share_id}`}
                            className="bg-orange-700 hover:bg-orange-800 text-stone-50 px-4 py-2.5 uppercase text-[10px] tracking-[0.24em]"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="text-stone-500 hover:text-orange-500 text-[10px] uppercase tracking-[0.24em] ink-link"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3
                            data-testid={`manage-title-${s.share_id}`}
                            className="font-serif-display text-3xl text-stone-100 leading-tight"
                          >
                            {s.title}
                          </h3>
                          <p className="mt-2 text-xs uppercase tracking-[0.24em] text-stone-500">
                            {s.recipe_ids?.length || 0} recipe{(s.recipe_ids?.length || 0) === 1 ? "" : "s"}
                            {s.created_at && (
                              <>
                                {" · "}
                                {new Date(s.created_at).toLocaleDateString(undefined, {
                                  year: "numeric", month: "short", day: "numeric",
                                })}
                              </>
                            )}
                          </p>
                          <p className="mt-3 text-xs font-mono-accent text-stone-500 truncate">
                            {shareUrl}
                          </p>
                        </>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => copyLink(s.share_id)}
                          data-testid={`manage-copy-${s.share_id}`}
                          className="inline-flex items-center gap-2 border border-stone-800 hover:border-orange-800 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-stone-300 hover:text-orange-500 transition-colors"
                        >
                          {copiedId === s.share_id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedId === s.share_id ? "Copied" : "Copy"}
                        </button>
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`manage-open-${s.share_id}`}
                          className="inline-flex items-center gap-2 border border-stone-800 hover:border-orange-800 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-stone-300 hover:text-orange-500 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open
                        </a>
                        <button
                          type="button"
                          onClick={() => setEditing({ share_id: s.share_id, title: s.title })}
                          data-testid={`manage-edit-${s.share_id}`}
                          className="inline-flex items-center gap-2 border border-stone-800 hover:border-orange-800 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-stone-300 hover:text-orange-500 transition-colors"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => revokeShare(s.share_id)}
                          data-testid={`manage-revoke-${s.share_id}`}
                          className="inline-flex items-center gap-2 border border-orange-900/50 hover:border-orange-700 px-3 py-2 text-[10px] uppercase tracking-[0.24em] text-orange-500 hover:text-orange-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Revoke
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CookbookManage;
